import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, Search, Plus, PackagePlus, WifiOff, CheckCheck } from 'lucide-react';
import { beverageProductsService, siteInventoryService } from '../services';
import { useToast } from '../hooks/use-toast';
import { getApiError } from '../lib/utils';

/**
 * AddFromMasterModal — search the inventory master, select one or more
 * products via checkboxes, assign them to this site in a single batch.
 *
 * iter372 — multi-select. Replaces the previous one-at-a-time UX:
 * users now tick any number of products, then click "Add N products"
 * in the sticky footer to fan-out assignments in a single batch.
 * Click anywhere on the row toggles its checkbox.
 *
 * Props:
 *   open: boolean
 *   onOpenChange: (open: boolean) => void
 *   siteId: number
 *   assignedProductIds: number[]    products already on this site
 *   onAssigned: (product) => void   called once per successfully assigned product
 *   offlineProducts?: Array         when provided, modal runs in offline mode
 *   onOfflineAssign?: (product) => Promise<void>  offline assign handler
 */
export default function AddFromMasterModal({
  open,
  onOpenChange,
  siteId,
  assignedProductIds = [],
  onAssigned,
  offlineProducts = null,
  onOfflineAssign = null,
}) {
  const { toast } = useToast();
  const isOfflineMode = Array.isArray(offlineProducts) && typeof onOfflineAssign === 'function';
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [query, setQuery] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  // iter367 — when ONLINE and the user is searching, fall back to a
  // server-side query so the result is never starved by a stale or
  // truncated client cache.
  const [serverResults, setServerResults] = useState(null);
  const searchSeqRef = React.useRef(0);

  const assignedSet = useMemo(() => new Set(assignedProductIds.map(Number)), [assignedProductIds]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setServerResults(null);
    setSelectedIds(new Set());
    setActiveSubcategory(null);  // iter426C — clear pill on each open
    if (isOfflineMode) {
      setProducts(offlineProducts || []);
      setLoading(false);
      return;
    }
    setLoading(true);
    beverageProductsService.listAllLite()
      .then(rows => setProducts(rows || []))
      .catch(err => toast({
        title: 'Failed to load inventory master',
        description: getApiError(err, 'Please try again'),
        variant: 'destructive',
      }))
      .finally(() => setLoading(false));
  }, [open, toast, isOfflineMode, offlineProducts]);

  useEffect(() => {
    if (!open || isOfflineMode) return;
    const q = (query || '').trim();
    if (!q) {
      setServerResults(null);
      return;
    }
    const seq = ++searchSeqRef.current;
    const t = setTimeout(async () => {
      try {
        const res = await beverageProductsService.list({
          search: q, page: 1, page_size: 200, lite: true,
        });
        if (seq !== searchSeqRef.current) return;
        const items = (res && res.items) || [];
        setServerResults(items);
      } catch {
        if (seq !== searchSeqRef.current) return;
        setServerResults(null);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [query, open, isOfflineMode]);

  // iter426C (#2) — Subcategory filter pill state. When the user
  // clicks a pill the filter narrows further; clicking the active
  // pill clears it.
  const [activeSubcategory, setActiveSubcategory] = useState(null);

  // Distinct subcategories present in the current product source —
  // computed once and re-used both for the pill bar and the filter.
  const subcategoryOptions = useMemo(() => {
    const counts = new Map();
    const source = !isOfflineMode && (query || '').trim() && Array.isArray(serverResults)
      ? serverResults
      : products;
    source.forEach(p => {
      if (assignedSet.has(Number(p.id))) return;
      const sc = (p.subcategory || '').trim();
      if (!sc) return;
      counts.set(sc, (counts.get(sc) || 0) + 1);
    });
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .slice(0, 16);  // cap pill bar; rest still searchable by name
  }, [products, serverResults, query, isOfflineMode, assignedSet]);

  const filtered = useMemo(() => {
    const q = (query || '').toLowerCase().trim();
    const source = !isOfflineMode && q && Array.isArray(serverResults)
      ? serverResults
      : products;
    return source
      .filter(p => !assignedSet.has(Number(p.id)))
      .filter(p => !activeSubcategory || (p.subcategory || '').trim() === activeSubcategory)
      .filter(p => !q
        || (p.product_name || '').toLowerCase().includes(q)
        || (p.product_code || '').toLowerCase().includes(q)
        || (p.brand || '').toLowerCase().includes(q)
        || (p.subcategory || '').toLowerCase().includes(q));
  }, [products, query, assignedSet, serverResults, isOfflineMode, activeSubcategory]);

  // iter426C — Render cap raised from 500 to 2000 so a fully loaded
  // 1500-product DB is browsable without forcing a search. The
  // virtualised list keeps render cost flat.
  const visible = filtered.slice(0, 2000);

  const toggleOne = (pid) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid); else next.add(pid);
      return next;
    });
  };

  const toggleAllVisible = () => {
    setSelectedIds(prev => {
      const allSelected = visible.length > 0 && visible.every(p => prev.has(p.id));
      if (allSelected) {
        const next = new Set(prev);
        visible.forEach(p => next.delete(p.id));
        return next;
      }
      const next = new Set(prev);
      visible.forEach(p => next.add(p.id));
      return next;
    });
  };

  const handleAssignSelected = async () => {
    if (selectedIds.size === 0) return;
    const idLookup = new Map();
    [...products, ...(serverResults || [])].forEach(p => { if (!idLookup.has(p.id)) idLookup.set(p.id, p); });
    const targets = [...selectedIds].map(id => idLookup.get(id)).filter(Boolean);
    if (targets.length === 0) return;
    setAssigning(true);
    try {
      if (isOfflineMode) {
        // Offline: queue sequentially so the parent's local cache stays
        // consistent (each onOfflineAssign mutates indexedDB).
        let ok = 0;
        for (const p of targets) {
          try { await onOfflineAssign(p); ok++; } catch { /* per-item failure tolerated */ }
        }
        toast({
          title: `Queued ${ok} ${ok === 1 ? 'product' : 'products'} (offline)`,
          description: 'They will sync to the central inventory when you reconnect.',
        });
        onOpenChange(false);
        return;
      }
      if (!siteId) return;
      // Online: fan out in parallel. addInventoryItem is idempotent on the
      // server (returns 200 / 409 collapsed) so retries are safe.
      const results = await Promise.allSettled(
        targets.map(p => siteInventoryService.addInventoryItem(siteId, {
          beverage_product_id: p.id,
          active_for_counting: true,
        }))
      );
      const succeeded = [];
      const failed = [];
      results.forEach((r, i) => {
        if (r.status === 'fulfilled') succeeded.push(targets[i]);
        else failed.push({ p: targets[i], err: r.reason });
      });
      succeeded.forEach(p => onAssigned && onAssigned(p));
      if (succeeded.length > 0) {
        toast({
          title: `Added ${succeeded.length} ${succeeded.length === 1 ? 'product' : 'products'}`,
          description: failed.length
            ? `${failed.length} failed and will need to be retried.`
            : 'All selected products are now on this client.',
        });
      }
      if (failed.length > 0 && succeeded.length === 0) {
        toast({
          title: 'Failed to add products',
          description: getApiError(failed[0].err, 'Try again'),
          variant: 'destructive',
        });
        return;
      }
      onOpenChange(false);
    } finally {
      setAssigning(false);
    }
  };

  const allVisibleSelected = visible.length > 0 && visible.every(p => selectedIds.has(p.id));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="add-from-master-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PackagePlus className="w-4 h-4" /> Add products from inventory master
          </DialogTitle>
          <DialogDescription>
            Search the inventory master, tick one or more products, then add them all in one go.
          </DialogDescription>
          {isOfflineMode && (
            <div
              className="flex items-center gap-2 px-2.5 py-1.5 mt-1 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-xs"
              data-testid="add-from-master-offline-banner"
            >
              <WifiOff className="w-3.5 h-3.5" />
              You are offline. Products will be queued and synced to the central inventory once you reconnect.
            </div>
          )}
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            autoFocus
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search by name, code, brand or subcategory..."
            className="pl-9 h-10"
            data-testid="add-from-master-search"
            autoComplete="off" autoCorrect="off" spellCheck={false} name="add-from-master-search-input"
          />
        </div>

        {/* iter426C (#2) — Subcategory filter pills. Top 16 by product
            count, with quick "Clear" affordance when one is active. */}
        {!loading && subcategoryOptions.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 -mt-1" data-testid="subcategory-pill-bar">
            <button
              type="button"
              onClick={() => setActiveSubcategory(null)}
              className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                activeSubcategory === null
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
              }`}
              data-testid="subcategory-pill-all"
            >
              All
            </button>
            {subcategoryOptions.map(([sc, n]) => (
              <button
                key={sc}
                type="button"
                onClick={() => setActiveSubcategory(prev => prev === sc ? null : sc)}
                className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                  activeSubcategory === sc
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                }`}
                data-testid={`subcategory-pill-${sc.replace(/\s+/g, '-').toLowerCase()}`}
              >
                {sc}
                <span className="ml-1 opacity-70">({n})</span>
              </button>
            ))}
          </div>
        )}

        {/* Select-all / select-none for the current visible list */}
        {!loading && visible.length > 0 && (
          <div className="flex items-center justify-between gap-3 text-xs px-1">
            <label className="inline-flex items-center gap-2 cursor-pointer select-none" data-testid="add-from-master-toggle-all">
              <Checkbox
                checked={allVisibleSelected}
                onCheckedChange={toggleAllVisible}
              />
              <span className="text-slate-700">
                {allVisibleSelected
                  ? `Deselect all ${visible.length}`
                  : `Select all ${visible.length}`}
              </span>
            </label>
            {selectedIds.size > 0 && (
              <span className="text-slate-500" data-testid="add-from-master-selected-count">
                {selectedIds.size} selected
              </span>
            )}
          </div>
        )}

        <div className="max-h-[400px] overflow-y-auto border rounded-lg">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
            </div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="text-center py-12 text-sm text-slate-500" data-testid="add-from-master-empty">
              {query
                ? `No master products match "${query}"`
                : 'All master products are already assigned to this client.'}
            </div>
          )}
          {!loading && visible.length > 0 && (
            <ul className="divide-y">
              {visible.map(p => {
                const checked = selectedIds.has(p.id);
                return (
                  <li
                    key={p.id}
                    className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-slate-50 ${checked ? 'bg-indigo-50/40' : ''}`}
                    onClick={() => toggleOne(p.id)}
                    data-testid={`add-from-master-row-${p.id}`}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => toggleOne(p.id)}
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`add-from-master-checkbox-${p.id}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">
                        {p.product_code && <span className="font-mono text-xs text-slate-500 mr-1.5">{p.product_code}</span>}
                        {p.product_name}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        {p.brand && <span className="text-xs text-slate-500">{p.brand}</span>}
                        {p.category && (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0">{p.category}</Badge>
                        )}
                        {p.default_unit_hint && (
                          <span className="text-[10px] text-slate-500 italic" data-testid={`add-from-master-unit-hint-${p.id}`}>
                            {p.default_unit_hint}
                          </span>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        {filtered.length > 2000 && (
          <p className="text-xs text-slate-500 text-center" data-testid="add-from-master-truncated">
            Showing first 2,000 of {filtered.length.toLocaleString()} unassigned products — refine your search or pick a subcategory pill.
          </p>
        )}
        {!loading && filtered.length > 0 && filtered.length <= 2000 && (
          <p className="text-xs text-slate-500 text-center" data-testid="add-from-master-result-count">
            {filtered.length.toLocaleString()} {filtered.length === 1 ? 'product' : 'products'} match
            {activeSubcategory ? ` in ${activeSubcategory}` : ''}{query ? ` for "${query}"` : ''}.
          </p>
        )}

        <DialogFooter className="pt-2 border-t">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={assigning}>
            Cancel
          </Button>
          <Button
            onClick={handleAssignSelected}
            disabled={selectedIds.size === 0 || assigning}
            data-testid="add-from-master-add-selected-btn"
          >
            {assigning
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Adding…</>
              : selectedIds.size > 1
                ? <><CheckCheck className="w-4 h-4 mr-2" />Add {selectedIds.size} products</>
                : <><Plus className="w-4 h-4 mr-2" />Add {selectedIds.size || ''} product</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
